
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import AsahiSelectHeader from '@/components/library/AsahiSelectHeader.vue'
import AsahiProgressBar from '@/components/library/assets/AsahiProgressBar.vue'
import { permissionSchema } from '@/store/modules/users/interfaces/IUser'
import { TransportationTypes } from '@/utils/transportTypes'
import { IAuthUser } from '@/store/modules/auth/interfaces/IAuthUser'
import DeliveryDatePicker from '@/components/cart/DeliveryDatePicker.vue'
import { getHolidayDays, isStandardDeliveryDate, validateDeliveryDate } from '@/utils/deliveryDate'
import shipToIcon from '@/components/library/assets/shipToBlack.svg'
import whiteShipToIcon from '@/components/library/assets/shipTo.svg'
import addToCartIcon from '@/components/library/assets/whitePlus.svg'
import suggestedCartIcon from '@/components/library/assets/star.svg'
import trackIcon from '@/components/library/assets/TruckBlack.svg'
import whiteTruckIcon from '@/components/library/assets/Truck.svg'
import calendarIcon from '@/components/library/assets/CalendarWhite.svg'
import { BaseCartQueryItem } from '@/store/modules/basket/interfaces/cartQuery/ICartQueryResponse'
import ContinueShoppingModal from '@/components/modals/ContinueShoppingModal.vue'
import modals from '@/utils/modals'
import formatDateMixin from '@/utils/mixin'
import authMixin from '@/utils/auth'
import AsahiButton from '@/components/library/AsahiButton.vue'

interface NavigateLink {
  name: string
  label: string
  href?: string
  link?: string
  target?: string
  isActive?: boolean
}

interface HeaderData {
  label: string
  children: any[]
  name: string
  href?: string
}

const MORE_SECTION_META_VALUES = ['marketing', 'news']
const CREATE_ORDER_NAME = 'create an order'

export default defineComponent({
  name: 'AsahiOrderInformation',
  components: { DeliveryDatePicker, AsahiSelectHeader, AsahiProgressBar, AsahiButton },
  mixins: [formatDateMixin, authMixin],
  props: {
    changeMessageColor: {
      type: Boolean,
      default: false
    },
    filterTransportations: {
      type: Array as () => Array<TransportationTypes>,
      default: undefined
    },
    isSuggestedCart: {
      type: Boolean,
      default: false
    },
    suggestedShipToId: {
      type: String,
      default: null
    },
    cartId: {
      type: String,
      default: ''
    },
    user: {
      type: Object,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      shipToIcon,
      whiteShipToIcon,
      whiteTruckIcon,
      trackIcon,
      calendarIcon,
      selectedShipmentId: '',
      errorMsg: '',
      successWeight: false,
      errorWeight: false,
      shipmentSet: false,
      selectedOrderType: 'selfpickup_selfpickup',
      selectedEmpties: '',
      deliveryDateSelected: '2025-02-20',
      shipTo: {},
      billingAddressForm: {} as any,
      options: [] as any,
      deliveryDatePickerHeight: 0,
      isExpanded: false,
      bufferedSelection: {} as any,
      isDatePickerOpen: false,
      orderInfo: null as any | null,
      addToCartIcon,
      suggestedCartIcon,
      addToCartLabel: '',
      hideCta: false
    }
  },
  computed: {
    ...mapState('basket', ['id', 'query', 'shipmentId', 'deliveryDate', 'items', 'paymentMethod', 'availableDate', 'availableDays', 'filteredDeliveryDates']),
    ...mapState('catalog', ['alignment', 'shipTos']),
    ...mapState('main', ['holidaysConfig']),
    ...mapState('auth', ['user']),

    emptiesOptions () {
      const hasEmptiesPermission = this.shipToAllPermission(this.user, this.selectedShipmentId).includes('Empties Management')
      return [
        {
          label: this.$t('Beer Order (Empty Return Order optional)'),
          value: '0',
          checked: this.selectedEmpties === '0'
        },
        ...hasEmptiesPermission
          ? [{
              label: this.$t('Empty return order'),
              value: '1',
              checked: this.selectedEmpties === '1'
            }]
          : []
      ]
    },

    isMoreSection (): boolean {
      return MORE_SECTION_META_VALUES.includes(this.$route?.meta?.tabSelection as string)
    },
    formattedDeliveryDate () {
      if (!this.deliveryDate) return ''
      const args = this.deliveryDate.split('-')
      return args[2] + '.' + args[1]
    },
    currentShippingPlace () {
      return this.shipToOptions.find((s: any) => s.value === this.selectedShipmentId.padStart(10, '0'))
    },
    currentShippingMethod () {
      return this.emptiesOptions.find((s: any) => s.value === this.selectedEmpties)
    },
    shipToOptions () {
      const { permissionSchema = [] } = this.user || {}
      return permissionSchema
        .filter((permission: any) =>
          permission.rpDetails.some((role: any) => role.permission.includes('Catalogue'))
        )
        .map((permission: any) => ({
          label: permission.shipToName,
          value: permission.shipToId,
          description: permission?.shipToAddress
        }))
    },
    progressBar () {
      return this.query?.progress_bar_data
    },
    maxOrderCash () {
      return this.progressBar?.iMaximumOrderCash ?? 0
    },
    grandTotalYourPrice () {
      return this.query?.grand_total_your_price_details?.grand_total_your_price ?? 0
    },
    cashFilledPercentage () {
      return (this.grandTotalYourPrice / this.maxOrderCash) * 100
    },
    messageColorClass () {
      let msgColor = ''
      let borderColor = ''
      if (this.changeMessageColor) {
        if (this.errorWeight === true) {
          msgColor = 'error-text'
          borderColor = 'border-error-text'
        } else if (this.successWeight === true) {
          msgColor = 'success-text'
          borderColor = 'border-success-text'
        } else {
          msgColor = 'default-text'
          borderColor = 'border-default-text'
        }
      }
      return { msgColor, borderColor }
    },
    cartItems () {
      let cartItems: Array<{productid: string}> = []
      if (this.items.length) {
        cartItems = this.items.map((item: BaseCartQueryItem) => {
          return { productId: item.product.sku }
        })
      }
      return cartItems
    },
    isOnHomepage () {
      return this.$route.path === '/'
    },
    isCartPage () {
      return this.$route.name === 'cart' || this.$route.path === '/cart'
    },
    defaultShipTo () {
      return this.$store.state.auth.defaultShipTo
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    isCatalogVisible (): boolean {
      return true
    },
    isOnSuggestedCart (): boolean {
      return this.$route?.path === '/catalog/suggested-cart'
    }
  },
  methods: {
    extractCatalogData (data: HeaderData[]) {
      const findByName = (list: HeaderData[]): HeaderData | undefined => {
        for (const item of list) {
          if (item?.name === CREATE_ORDER_NAME) return item
          if (item?.children?.length) {
            const found = findByName(item.children)
            if (found) return found
          }
        }
      }
      const target = findByName(data)
      this.addToCartLabel = target?.label || ''
    },
    navigateToPath (link: NavigateLink) {
      this.$gtm.trackEvent({ ecommerce: null })
      this.$gtm.trackEvent({
        event: 'click_interaction',
        action: link.label,
        component: 'header'
      })
      this.$navigate(link.href || link.link || '')
    },
    async onEmptiesSelect (value: string) {
      localStorage.setItem('onlyEmpties', value)
      this.$store.commit('basket/SET_ONLY_EMPTIES', value, { root: true })
      this.selectedEmpties = value
      if (this.$route.name === 'favorites' && this.selectedEmpties !== '1') {
        await this.$store.dispatch('catalog/FETCH_FAVORITES', { pageSize: 25 }, { root: true })
      } else {
        await this.$store.dispatch('catalog/FETCH_PRODUCTS', { fetchPolicy: 'network-only' }, { root: true })
      }
      if (this.$route.name === 'favorites' ||
          this.$route.name === 'products') {
        this.$navigate(`/catalog/${this.$route.name}`)
      }
    },

    onEmptiesSelectWrapper (value: string) {
      if (this.selectedEmpties !== value && this.items.length > 0) {
        if (this.selectedEmpties === '0') {
          this.setContinueModalMessages(
            this.$t('Do you want to proceed with the current order or start a new one?'),
            this.$t('Continue with current beer order and keep products already in your cart'),
            this.$t('Start an empty order')
          )
        } else {
          this.setContinueModalMessages(
            this.$t('Do you want to proceed with the current order or start a new one?'),
            this.$t('Continue with current empty order and keep products already in your cart'),
            this.$t('Start a beer order')
          )
        }
        this.bufferedSelection = { type: 'onEmptiesSelectWrapper', value }
        this.openOrderModal()
      } else {
        this.onEmptiesSelect(value)
      }
    },

    handleDatePickerOpen () {
      this.isDatePickerOpen = true
    },
    handleDatePickerClose () {
      this.isDatePickerOpen = false
    },
    toggleAccordion (event: any) {
      this.isExpanded = event.target.getAttribute('aria-expanded') === 'true'
    },
    setShipmentWrapper (value: string) {
      if (this.items?.length > 0) {
        this.setContinueModalMessages(
          this.$t('You have a basket in progress for ship to with the date', { place: this.currentShippingPlace?.label || '', date: this.formateDateWithoutLocale(this.deliveryDateSelected) }),
          this.$t('Continue ordering with current ship to', { place: this.currentShippingPlace?.label || '' }),
          this.$t('Start a new order with the new ship to. Products from cart will be removed', { place: this.getShippingDescription(value) }))
        this.bufferedSelection = { type: 'setShipment', value }
        return this.openOrderModal()
      } else {
        this.setContinueModalMessages('', '', '')
        this.setShipment(value, true)
      }
    },

    getShippingDescription (value: string) {
      const shipTo = this.shipToOptions.find((option: any) => option.value === value)
      return shipTo?.label || ''
    },

    async setShipment (value: string, isDropDownChange = false) {
      this.errorMsg = ''
      this.selectedShipmentId = value
      this.errorMsg = ''
      if (this.checkDisableShipTo(this.user, this.selectedShipmentId)) {
        this.errorMsg = this.$t('ShipTo suspended - Select another ShipTo')
        return
      }
      try {
        await this.$store.commit('basket/SET_LOADING', true, { root: true })
        await this.$store.dispatch(
          'catalog/FETCH_SHIP_TO',
          { ship_to_id: this.selectedShipmentId },
          { root: true }
        )
        if (this.shipTos) {
          this.shipmentSet = true
          this.setDefaultTransportType()
        }
        if (this.isOnHomepage || this.isCartPage) {
          await this.$store.dispatch('catalog/FETCH_RECOMMENDED_PRODUCTS', {
            shipTo: this.selectedShipmentId,
            cartItems: this.cartItems
          }, { root: true })
        }
        await this.$store.dispatch('basket/FETCH_SUGGESTED_CART', { ship_tos: [this.selectedShipmentId], group_view: false })
      } catch (error) {
        console.error('shipment error', error)
        this.errorMsg = 'Error setting shipment'
      } finally {
        await this.$store.commit('basket/SET_LOADING', false, { root: true })
      }

      const companyId = this.getCompanyid(this.selectedShipmentId, this.user)
      const event = new CustomEvent('setShipment', {
        detail: {
          type: 'delivery-options',
          shipTo: this.selectedShipmentId,
          payer: companyId
        }
      })
      document.dispatchEvent(event)
    },

    setDefaultTransportType () {
      this.selectedOrderType = 'freeshipping_freeshipping'
      this.onOrderTransportTypeSelection(this.selectedOrderType, false)
    },

    getCompanyid (shipToId: string, user: IAuthUser) {
      let companyId = ''
      const shipToItem = user?.permissionSchema.find((item: permissionSchema) => item.shipToId === shipToId)
      if (shipToItem) {
        companyId = shipToItem?.companyId
      }
      return companyId
    },
    checkDisableShipTo (user: IAuthUser, shipToId: string) {
      const permissionSchema = user?.permissionSchema || []

      if (shipToId != null) {
        return permissionSchema.some(
          (shipToItem: permissionSchema) => shipToItem.shipToId === shipToId && shipToItem.shipToStatus === 'SUSPENDED'
        )
      }

      if (permissionSchema.length === 1) {
        return permissionSchema[0]?.shipToStatus === 'SUSPENDED'
      }

      return false
    },
    async startYourOrder () {
      const errorMsg = []
      if (this.selectedOrderType === '') errorMsg.push(this.$t('Order type has to not be empty!'))
      if (this.deliveryDateSelected === '') errorMsg.push(this.$t('Delivery date has to not be empty!'))
      if (errorMsg.length > 0) {
        return
      }
      if (!validateDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos, getHolidayDays(this.holidaysConfig))) {
        (this.$refs.deliveryDatePickerRef as InstanceType<typeof DeliveryDatePicker>)?.forceRecalculate()
        errorMsg.push(this.$t('Shipping cannot be ordered. Choose a different date.'))
      }
      if (errorMsg.length > 0) {
        this.errorMsg = errorMsg[0]
        return
      }

      // get new orderable units based on new order type
      await this.$store.dispatch('main/FETCH_ORDERABLE_UNITS_CONFIG',
        {
          customerType: localStorage.getItem('customertype'),
          orderType: this.selectedOrderType
        }, { root: true })

      localStorage.setItem('deliveryDate', this.deliveryDateSelected)
      setTimeout(() => {
        localStorage.setItem('standardDeliveryDate', Number(isStandardDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos)).toString())
      }, 1000)

      this.$store.commit('basket/SET_DELIVERY_DATE', this.deliveryDateSelected, { root: true })
      await this.$store.dispatch(
        'basket/FETCH_TRANSPORT_TYPE',
        { transporttype: this.selectedOrderType, cart_id: this.id },
        { root: true }
      )

      try {
        if (this.isSuggestedCart) {
          this.onProceedWithSuggestedCart()
        } else {
          const ajaxRequests = []
          if (this.id) {
            ajaxRequests.push(
              this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.id }, { root: true })
            )
          }

          if (!this.isOnHomepage) {
            ajaxRequests.push(this.$store.dispatch('catalog/FETCH_PRODUCTS', { fetchPolicy: 'network-only' }, { root: true }))
          }

          ajaxRequests.push(
            this.$store.dispatch('basket/FETCH_SUGGESTED_CART', { ship_tos: [this.selectedShipmentId] })
          )

          await Promise.all(ajaxRequests)

          this.$store.dispatch('catalog/FETCH_WISHLIST', { bypassloading: true }, { root: true })
        }
      } catch (error) {
        console.error('order modal error', error)
      }
    },
    async initDefaultShipmentId () {
      // Wait for shipToOptions to be populated (already done before calling this)
      const shipTos = this.shipToOptions
      const savedShipToId = localStorage.getItem('shipToId') ?? ''

      if (shipTos.length === 1) {
        // If only one option, always use it and override saved localStorage
        this.selectedShipmentId = shipTos[0].value
        localStorage.setItem('shipToId', this.selectedShipmentId)
      } else if (savedShipToId && shipTos.some((option: { value: string }) => option.value === savedShipToId)) {
        // If multiple options, use saved only if it's valid
        this.selectedShipmentId = savedShipToId
      } else if (shipTos.length > 0) {
        // Fallback to first valid option if saved one is not available
        this.selectedShipmentId = shipTos[0].value
        localStorage.setItem('shipToId', this.selectedShipmentId)
      }

      await this.setShipment(this.selectedShipmentId)

      if (!this.holidaysConfig || this.holidaysConfig?.holidays?.length === 0) {
        await this.$store.dispatch('main/FETCH_HOLIDAYS_CONFIG', { root: true })
      }
    },

    async onOrderTransportTypeSelection (value: string, isDropDownChange = false) {
      const alreadySetDeliveryDate = localStorage.getItem('deliveryDate') ?? ''
      const fromLocal = localStorage.getItem('shipToId') || this.shipToOptions[0].value
      const selectedShipTo = fromLocal || this.selectedShipmentId
      this.selectedOrderType = value
      await this.$store.commit('basket/SET_SHIPMENT_ID', this.selectedOrderType, { root: true })
      const storeId = localStorage.getItem('store')
      if (this.selectedOrderType != null && storeId != null && selectedShipTo) {
        await this.$store.dispatch('basket/GET_DELIVERY_AVAILABLE_DATE', {
          shipTo: selectedShipTo,
          shipmentId: this.selectedOrderType,
          store: storeId
        })

        // Check is already selected delivery date exist in available days array
        const doesExistPreSelectedDate = this.availableDays.filter((d: string) => alreadySetDeliveryDate !== '' && d.includes(alreadySetDeliveryDate))
        if (doesExistPreSelectedDate.length > 0 && !isDropDownChange) {
          this.deliveryDateSelected = alreadySetDeliveryDate
        } else {
          const firstAvailableDate = this.filteredDeliveryDates.length > 0 ? this.filteredDeliveryDates[0] : new Date().toISOString()
          this.deliveryDateSelected = firstAvailableDate.slice(0, 10)
        }
        localStorage.setItem('deliveryDate', this.deliveryDateSelected)
        localStorage.setItem(
          'standardDeliveryDate',
          Number(isStandardDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos)).toString()
        )

        this.startYourOrder()
        await this.$store.commit('basket/SET_TRANSPORT_TYPE', {
          transporttype: this.computedSelectedOrderType
        })
        if (this.$route.name === 'favorites') {
          await this.$store.dispatch('catalog/FETCH_FAVORITES', { pageSize: 25 }, { root: true })
        }
      }
    },

    openOrderModal () {
      modals.showModal({
        component: ContinueShoppingModal,
        listeners: {
          doSelection: () => {
            this.doSelection()
            this.setContinueModalMessages('', '', '')
          }
        }
      })
    },

    setContinueModalMessages (title: string, continueMsg: string, newMsg: string) {
      this.$store.commit('catalog/SET_CONTINUE_MODAL_TITLE', title)
      this.$store.commit('catalog/SET_CONTINUE_MODAL_CONTINUE_MESSAGE', continueMsg)
      this.$store.commit('catalog/SET_CONTINUE_MODAL_NEW_MESSAGE', newMsg)
    },

    doSelection () {
      if (this.bufferedSelection?.type && this.bufferedSelection.value) {
        switch (this.bufferedSelection?.type) {
          case 'onEmptiesSelectWrapper' :
            this.onEmptiesSelect(this.bufferedSelection.value)
            break
          case 'setShipment' :
            this.setShipment(this.bufferedSelection.value, true)
            break
          case 'onDateSelection' :
            this.onDateSelection(this.bufferedSelection.value)
            break
        }
      }
      this.bufferedSelection = {}
    },

    async onProceedWithSuggestedCart () {
      await this.$store.dispatch('basket/CREATE_SUGGESTED_CART', {
        shipTo: this.selectedShipmentId,
        cartId: ''
      }, { root: true })
    },
    onDateSelectionWrapper (valueWrapper: any) {
      const value = typeof valueWrapper === 'string' ? valueWrapper : valueWrapper.value
      this.deliveryDateSelected = localStorage.getItem('deliveryDate') ?? this.filteredDeliveryDates[0]
      if (this.items?.length > 0 && !valueWrapper.isFirstAvailable) {
        (this.$refs.deliveryDatePickerRef as InstanceType<typeof DeliveryDatePicker>)?.forceRecalculate()
        this.setContinueModalMessages(
          this.$t('You have a basket in progress for the ship to with the date of delivery', { place: this.currentShippingPlace?.description || '', date: this.formateDateWithoutLocale(this.deliveryDateSelected) }),
          this.$t('Continue ordering with current date.'),
          this.$t('Start a new order with delivery date. Products from the cart will be removed.', { date: this.formateDateWithoutLocale(value) }))
        this.bufferedSelection = { type: 'onDateSelection', value }
        return this.openOrderModal()
      } else {
        this.setContinueModalMessages('', '', '')
        this.onDateSelection(value)
      }
    },
    onDateSelection (date: string) {
      this.deliveryDateSelected = date
      localStorage.setItem('deliveryDate', this.deliveryDateSelected)
      localStorage.setItem(
        'standardDeliveryDate',
        Number(isStandardDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos)).toString()
      )
      this.$store.commit('basket/SET_DELIVERY_DATE', this.deliveryDateSelected, { root: true })
      this.$store.commit('catalog/SET_SHOULD_UPDATE_PDP', true, { root: true })
      this.startYourOrder()
    },
    computedSelectedOrderType (): string {
      if (!this.orderInfo || !this.orderInfo.deliveryTransportId) return ''

      switch (this.orderInfo.deliveryTransportId) {
        case 'YDOR':
          return 'selfpickup_selfpickup'
        case 'YSE1':
          return 'emptiesfulltruck_emptiesfulltruck'
        case 'YDE1':
          return 'emptiespickup_emptiespickup'
        case 'YSOR':
          return 'flatrate_flatrate'
        default:
          return ''
      }
    }

  },
  mounted () {
    const selected = localStorage.getItem('onlyEmpties')
    if (selected) {
      this.onEmptiesSelect(selected)
    } else {
      this.onEmptiesSelect('0')
    }

    const shipToId = localStorage.getItem('shipToId')
    const defaultShipmentId = this.defaultShipTo ?? ''
    if (!shipToId && defaultShipmentId && this.shipToOptions.length > 1) {
      localStorage.setItem('shipToId', defaultShipmentId)
    }

    this.initDefaultShipmentId()
    this.$store.commit('basket/SET_SHIPMENT_ID', this.selectedOrderType, { root: true })
    this.extractCatalogData(this.portalPageMenuLink)
  },
  watch: {
    availableDate (newDate) {
      this.deliveryDate = newDate
    },
    $route: {
      handler (nextVal) {
        this.hideCta = nextVal?.meta?.hideCreateOrderButton as boolean || false
      },
      immediate: true,
      deep: true
    },
    portalPageMenuLink (newData) {
      this.extractCatalogData(newData)
    }
  }
})
